import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClient } from "../api";
import { isResponseSuccess } from "../utils";
import { URLS } from "../constants";
import { BillItem, BillType, DEVICE_TYPE, ITableLock, ITables, KITCHEN_ITEM_STATUS, TABLE_STATUS } from "../all-types";

/**
 * Fetch all tables from the backend.
 */
export const getTableList = createAsyncThunk(
  "tables/getTableList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(URLS.GET_TABLES);
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to fetch tables");
      }
      return response.data.data as ITables[];
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not fetch tables");
    }
  }
);


export const getTableById = createAsyncThunk(
  "tables/getTableById",
  async (tableId: string, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(URLS.GET_TABLE(tableId));
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to fetch table");
      }
      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not fetch table");
    }
  }
);

/**
 * Create a new table entry.
 */
export const addRestaurantTable = createAsyncThunk(
  "tables/addRestaurantTable",
  async (table: Omit<ITables, 'tableId'>, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post(URLS.ADD_TABLE, { table });
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to add table");
      }
      dispatch(getTableList()); // Refresh list after adding
      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not add new table");
    }
  }
);

/**
 * Update an existing table.
 */
export const updateRestaurantTable = createAsyncThunk(
  "tables/updateRestaurantTable",
  async (details: { id: string; table: Partial<ITables> }, { dispatch, rejectWithValue }) => {
    try {
      console.log('Updating table', "updateRestaurantTable");
      const response = await axiosClient.put(URLS.UPDATE_TABLE(details.id), { table: details.table });
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to update table");
      }
      dispatch(getTableList()); // Refresh list after updating
      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not update table");
    }
  }
);

/**
 * Delete a table.
 */
export const deleteRestaurantTable = createAsyncThunk(
  "tables/deleteRestaurantTable",
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      console.log('Deleting table', id, "deleteRestaurantTable");
      const response = await axiosClient.delete(URLS.DELETE_TABLE(id));
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to delete table");
      }
      dispatch(getTableList()); // Refresh list after deleting
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not delete table");
    }
  }
);



// Cart updates replace the whole table document. If two cart PUTs run at the
// same time, the later response can be based on stale local state and restore
// a previously deleted item. Serialize mutations per table so rapid deletes
// reach the server in the same order the user performed them.
const _cartUpdateChain = new Map<string, Promise<unknown>>();
const _cartUpdateSeq = new Map<string, number>();

export const updateCartItemOfTheTable = createAsyncThunk(
  "tables/updateCartItemOfTheTable",
  async (
    details: { table: ITables},
    { getState, dispatch, rejectWithValue }
  ) => {
    const tableId = details.table?._id || "";

    // P1-9: never let a whole-table PUT carry an *empty* cart. Legitimate
    // emptying happens via removeTableItem (last item) or close
    // (billSession: null) — both still work below. This guards the
    // injected display `defaultBillType` (items: []) from wiping a real
    // server session.
    const session: any = (details.table as any)?.billSession;
    if (session && Array.isArray(session.items) && session.items.length === 0) {
      return null as unknown as ITables;
    }

    const mySeq = (_cartUpdateSeq.get(tableId) || 0) + 1;
    if (tableId) _cartUpdateSeq.set(tableId, mySeq);
    const isStale = () => tableId && _cartUpdateSeq.get(tableId) !== mySeq;

    const run = async (): Promise<ITables> => {
      const state = getState() as any;
      const userId = state?.authentication?.userId || "";
      const cachedTable: ITables | undefined = (state?.preloadData?.tables as ITables[] | undefined)?.find(
        (t) => t?._id === tableId
      );
      const cachedLock = cachedTable?.tableLock;
      if (cachedLock?.lockedBy && cachedLock.lockedBy !== userId) {
        throw { locked: true, lockInfo: cachedLock as ITableLock };
      }

      // P0-2: send the version this edit was based on so the server can
      // reject lost updates with 409. Only for item-bearing saves — the
      // close/free path (billSession: null) must always succeed, so it is
      // left unversioned. P0-3: send the lock id we believe we hold.
      const expectedUpdatedAt = session?.items?.length
        ? (details.table as any)?.updatedAt
        : undefined;

      console.log('Updating cart item for table', tableId, "updateCartItemOfTheTable");

      if(tableId === "999") return null as unknown as ITables; // Mock table for UI testing, skip server call 

      const response = await axiosClient.put(
        URLS.UPDATE_TABLE(tableId),
        { table: details.table, lockedBy: userId || undefined, expectedUpdatedAt },
        { skipGlobalLoader: true } as any
      );

      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to update table");
      }

      return response.data.data as ITables;
    };

    try {
      const previous = tableId ? _cartUpdateChain.get(tableId) : null;
      const next = (previous ? previous.catch(() => undefined) : Promise.resolve()).then(run);
      if (tableId) {
        _cartUpdateChain.set(tableId, next);
        next.finally(() => {
          if (_cartUpdateChain.get(tableId) === next) _cartUpdateChain.delete(tableId);
        });
      }
      const result = await next;
      if (isStale()) {
        // A newer cart PUT has already superseded this response. Reducers
        // treat null as "do not merge", which avoids briefly restoring items
        // from an older server snapshot.
        return null as unknown as ITables;
      }
      return result;
    } catch (error: any) {
      if (isStale()) {
        return null as unknown as ITables;
      }
      // P0-2 / P0-3: the server rejected the write (version conflict or a
      // lock now held by someone else). Re-fetch the authoritative table so
      // the cart reflects server truth, then defer — for a pure version
      // conflict we return null (no alert; user can simply redo), and for a
      // lock conflict we surface the lock info so the UI can explain.
      if (error?.response?.status === 409) {
        if (tableId) dispatch(getTableById(tableId));
        const data = error?.response?.data?.data;
        if (data?.lockedBy) {
          return rejectWithValue({ locked: true, lockInfo: data as ITableLock });
        }
        return null as unknown as ITables;
      }
      if (error && typeof error === "object" && "locked" in error) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message || "Could not update table");
    }
  }
);


export const lockTable = createAsyncThunk(
  "tables/lockTable",
  async (
    details: { tableId: string; lockedBy?: string; lockedByName?: string; deviceType: DEVICE_TYPE },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.post(
        URLS.LOCK_TABLE(details.tableId),
        {
          lockedBy: details.lockedBy,
          lockedByName: details.lockedByName,
          deviceType: details.deviceType,
        },
        { skipGlobalLoader: true } as any
      );
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to lock table");
      }
      // Reducer's addCase(lockTable.fulfilled) already merges the updated
      // table into preloadData.tables. Refetching the entire /tables list
      // here just to refresh one table caused a noticeable hitch when
      // opening a table. Other tables stay in sync via socket events /
      // user-triggered refresh.
      return response.data.data as ITables;
    } catch (error: any) {
      const lockInfo = error?.response?.data?.data;
      if (error?.response?.status === 409 && lockInfo) {
        return rejectWithValue({ locked: true, lockInfo: lockInfo as ITableLock });
      }
      return rejectWithValue(error.message || "Could not lock table");
    }
  }
);

export const unlockTable = createAsyncThunk(
  "tables/unlockTable",
  async (
    details: { tableId: string; lockedBy?: string; force?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.delete(URLS.UNLOCK_TABLE(details.tableId), {
        data: { lockedBy: details.lockedBy, force: details.force ?? false },
        skipGlobalLoader: true,
      } as any);
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to unlock table");
      }
      // Same reasoning as lockTable: addCase(unlockTable.fulfilled) merges
      // the updated table; the broad /tables refetch is unnecessary.
      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not unlock table");
    }
  }
);

export const updateTableItemStatus = createAsyncThunk(
  "tables/updateTableItemStatus",
  async (
    details: { tableId: string, sno: number, status: KITCHEN_ITEM_STATUS },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosClient.put(
        URLS.UPDATE_TABLE_ITEM(details.tableId, details.sno, details.status),
        undefined,
        { skipGlobalLoader: true } as any
      );

      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to update table item status");
      }

      return response.data.data as ITables;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not update table item status");
    }
  }
);

// Shared runner for the granular single-item endpoints. Reuses the same
// per-table chaining + sequencing as the whole-table PUT so rapid successive
// item mutations still reach the server in order and superseded responses are
// dropped. Crucially, each request carries only one item, so it can never
// overwrite another item's kitchen status (the original bug).
const runChainedItemMutation = async (
  tableId: string,
  getState: () => unknown,
  rejectWithValue: (v: any) => any,
  // `lockedBy` is the id we believe holds the table lock; the request builder
  // forwards it so the server can enforce the lock (P0-3).
  request: (lockedBy: string) => Promise<any>
): Promise<ITables> => {
  const mySeq = (_cartUpdateSeq.get(tableId) || 0) + 1;
  if (tableId) _cartUpdateSeq.set(tableId, mySeq);
  const isStale = () => tableId && _cartUpdateSeq.get(tableId) !== mySeq;

  const run = async (): Promise<ITables> => {
    const state = getState() as any;
    const userId = state?.authentication?.userId || "";
    const cachedTable: ITables | undefined = (state?.preloadData?.tables as ITables[] | undefined)?.find(
      (t) => t?._id === tableId
    );
    const cachedLock = cachedTable?.tableLock;
    if (cachedLock?.lockedBy && cachedLock.lockedBy !== userId) {
      throw { locked: true, lockInfo: cachedLock as ITableLock };
    }

    const response = await request(userId);
    if (!isResponseSuccess(response.status)) {
      throw new Error("Failed to update table item");
    }
    return response.data.data as ITables;
  };

  try {
    const previous = tableId ? _cartUpdateChain.get(tableId) : null;
    const next = (previous ? previous.catch(() => undefined) : Promise.resolve()).then(run);
    if (tableId) {
      _cartUpdateChain.set(tableId, next);
      next.finally(() => {
        if (_cartUpdateChain.get(tableId) === next) _cartUpdateChain.delete(tableId);
      });
    }
    const result = await next;
    if (isStale()) return null as unknown as ITables;
    return result;
  } catch (error: any) {
    if (isStale()) return null as unknown as ITables;
    // P0-3: server rejected because the lock is held elsewhere. Map it to the
    // same shape as the client-side guard so the UI can show "locked by X".
    if (error?.response?.status === 409) {
      const data = error?.response?.data?.data;
      return rejectWithValue({ locked: true, lockInfo: data as ITableLock });
    }
    if (error && typeof error === "object" && "locked" in error) {
      return rejectWithValue(error);
    }
    return rejectWithValue(error?.message || "Could not update table item");
  }
};

// Append a single new item to the table's cart.
export const addTableItem = createAsyncThunk(
  "tables/addTableItem",
  async (
    details: { tableId: string; item: BillItem; billSession?: any },
    { getState, rejectWithValue }
  ) => {
    return runChainedItemMutation(details.tableId, getState, rejectWithValue, (lockedBy) =>
      axiosClient.post(
        URLS.ADD_TABLE_ITEM(details.tableId),
        { item: details.item, billSession: details.billSession, lockedBy: lockedBy || undefined },
        { skipGlobalLoader: true } as any
      )
    );
  }
);

// Replace a single existing item (matched by sno) after an edit.
export const updateTableItem = createAsyncThunk(
  "tables/updateTableItem",
  async (
    details: { tableId: string; sno: number; item: BillItem },
    { getState, rejectWithValue }
  ) => {
    return runChainedItemMutation(details.tableId, getState, rejectWithValue, (lockedBy) =>
      axiosClient.put(
        URLS.UPDATE_TABLE_ITEM_DETAILS(details.tableId, details.sno),
        { item: details.item, lockedBy: lockedBy || undefined },
        { skipGlobalLoader: true } as any
      )
    );
  }
);

// Remove a single item (matched by sno) from the table's cart.
export const removeTableItem = createAsyncThunk(
  "tables/removeTableItem",
  async (
    details: { tableId: string; sno: number },
    { getState, rejectWithValue }
  ) => {
    return runChainedItemMutation(details.tableId, getState, rejectWithValue, (lockedBy) =>
      axiosClient.delete(URLS.DELETE_TABLE_ITEM(details.tableId, details.sno), {
        data: { lockedBy: lockedBy || undefined },
        skipGlobalLoader: true,
      } as any)
    );
  }
);
